// src/components/KindOfTrip.tsx

import React, { useState } from 'react';
import { FaUserAlt, FaUsers, FaHeart, FaChild } from 'react-icons/fa';

// Import normal images
import MountainImage from '../../images/Mountain.png';
import BeachImage from '../../images/Beach.png';
import CityImage from '../../images/City.png';
import MatesImage from '../../images/Mates.png';
import SoloImage from '../../images/Solo.png';
import NatureImage from '../../images/Nature.png';

// Import "blue" versions of the images (with space in filename)
import MountainImageBlue from '../../images/Mountain blue.png';
import BeachImageBlue from '../../images/Beach blue.png';
import CityImageBlue from '../../images/City blue.png';
import MatesImageBlue from '../../images/Mates blue.png';
import SoloImageBlue from '../../images/Solo blue.png';
import NatureImageBlue from '../../images/Nature blue.png';

// Import the new images
import PhoneImage from '../../images/Phone.png';
import ThailandImage from '../../images/Thailand.png';

// Define the TripType type
type TripType = 'manual' | 'ai-assist' | 'feeling-lucky';

interface KindOfTripProps {
  tripType: TripType;
  onTripTypeChange: (type: TripType) => void;
  options: {
    value: TripType;
    label: string;
  }[];
}

/**
 * KindOfTrip Component
 *
 * This component allows users to select different types of trips.
 * Selected trip types have their images switched to their "blue" versions.
 * Additionally, two new images and corresponding texts are displayed above the trip selection.
 */
const KindOfTrip: React.FC<KindOfTripProps> = ({ tripType, onTripTypeChange, options }) => {
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]); // Allow multiple selections
  const [selectedTripType, setSelectedTripType] = useState<string | null>(null);

  // Handle image click to toggle selection
  const handleImageClick = (tripType: string) => {
    setSelectedTrips((prevSelectedTrips) => {
      if (prevSelectedTrips.includes(tripType)) {
        return prevSelectedTrips.filter((trip) => trip !== tripType);
      } else {
        return [...prevSelectedTrips, tripType];
      }
    });
  };

  const getImageSource = (tripType: string, normalImage: string, blueImage: string) => {
    return selectedTrips.includes(tripType) ? blueImage : normalImage;
  };

  const isSelected = (tripType: string) => {
    return selectedTrips.includes(tripType);
  };

  const handleTripTypeSelect = (type: string) => {
    setSelectedTripType(type);
  };

  return (
    <div className="max-w-7xl mx-auto px-8 py-6">
      
      {/* Header Text */}
      <div className="text-center mb-4">
        <h1 className="text-3xl md:text-5xl font-bold mb-2">Don't holi-daze, holiday!</h1>
        <p className="text-lg md:text-2xl text-black px-4">Take a look at our latest deals and inspiration to help you push the button</p>
      </div>

      {/* Image Row - Mobile shows only iPhone, Desktop shows both */}
      <div className="flex justify-center gap-12 mb-4">
        <div className="w-full md:w-1/3 max-w-lg">
          <img src={PhoneImage} alt="Phone" className="w-full h-auto rounded-lg shadow-md" />
        </div>
        <div className="hidden md:block w-1/3 max-w-lg">
          <img src={ThailandImage} alt="Thailand" className="w-full h-auto rounded-lg shadow-md" />
        </div>
      </div>

      {/* Trip Types Section - Hidden on mobile */}
      <div className="hidden md:block">
        <h2 className="text-4xl font-semibold text-center mb-10">What kind of trip?</h2>
        
        {/* Trip Type Selection */}
        <div className="grid grid-cols-4 gap-5 px-5 max-w-3xl mx-auto mb-16">
        <button 
          onClick={() => handleTripTypeSelect('solo')}
          className={`flex items-center justify-center gap-3 w-full p-4 border ${selectedTripType === 'solo' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'} rounded-xl bg-white text-black text-base font-medium hover:translate-y-[-2px] hover:shadow-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-300 relative overflow-hidden`}
        >
          <FaUserAlt className="text-icon-blue text-xl transition-all duration-300 group-hover:scale-110" />
          <span className="font-buenos-aires">Solo Trip</span>
        </button>
        <button 
          onClick={() => handleTripTypeSelect('partner')}
          className={`flex items-center justify-center gap-3 w-full p-4 border ${selectedTripType === 'partner' ? 'border-red-500 bg-red-50' : 'border-gray-200'} rounded-xl bg-white text-black text-base font-medium hover:translate-y-[-2px] hover:shadow-lg hover:border-red-500 hover:bg-red-50 transition-all duration-300 relative overflow-hidden`}
        >
          <FaHeart className="text-icon-red text-xl transition-all duration-300 group-hover:scale-110" />
          <span className="font-buenos-aires">Partner trip</span>
        </button>
        <button 
          onClick={() => handleTripTypeSelect('friends')}
          className={`flex items-center justify-center gap-3 w-full p-4 border ${selectedTripType === 'friends' ? 'border-green-500 bg-green-50' : 'border-gray-200'} rounded-xl bg-white text-black text-base font-medium hover:translate-y-[-2px] hover:shadow-lg hover:border-green-500 hover:bg-green-50 transition-all duration-300 relative overflow-hidden`}
        >
          <FaUsers className="text-icon-green text-xl transition-all duration-300 group-hover:scale-110" />
          <span className="font-buenos-aires">Friends Trip</span>
        </button>
        <button 
          onClick={() => handleTripTypeSelect('family')}
          className={`flex items-center justify-center gap-3 w-full p-4 border ${selectedTripType === 'family' ? 'border-amber-500 bg-amber-50' : 'border-gray-200'} rounded-xl bg-white text-black text-base font-medium hover:translate-y-[-2px] hover:shadow-lg hover:border-amber-500 hover:bg-amber-50 transition-all duration-300 relative overflow-hidden`}
        >
          <FaChild className="text-icon-amber text-xl transition-all duration-300 group-hover:scale-110" />
          <span className="font-buenos-aires">Family trip</span>
        </button>
      </div>
      
      {/* Activity Types Section */}
      <h3 className="text-2xl font-semibold text-center mb-8">What activities are you interested in?</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('Mountain') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('Mountain')}
        >
          <img src={getImageSource('Mountain', MountainImage, MountainImageBlue)} alt="Mountain" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">Mountain</span>
        </div>
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('Beach') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('Beach')}
        >
          <img src={getImageSource('Beach', BeachImage, BeachImageBlue)} alt="Beach" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">Beach</span>
        </div>
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('City') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('City')}
        >
          <img src={getImageSource('City', CityImage, CityImageBlue)} alt="City" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">City</span>
        </div>
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('Mates') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('Mates')}
        >
          <img src={getImageSource('Mates', MatesImage, MatesImageBlue)} alt="Mates" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">Mates</span>
        </div>
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('Solo') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('Solo')}
        >
          <img src={getImageSource('Solo', SoloImage, SoloImageBlue)} alt="Solo" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">Solo</span>
        </div>
        <div
          className={`flex flex-col items-center p-6 rounded-lg cursor-pointer transition-all duration-300 hover:shadow-lg
            ${isSelected('Nature') ? 'bg-blue-50 shadow-md' : 'bg-transparent'}`}
          onClick={() => handleImageClick('Nature')}
        >
          <img src={getImageSource('Nature', NatureImage, NatureImageBlue)} alt="Nature" className="w-20 h-20 mb-3" />
          <span className="text-xl font-medium">Nature</span>
        </div>
      </div>
      </div> {/* End of hidden md:block div */}
    </div>
  );
};

export default KindOfTrip;
